import { useState, useEffect } from 'react';
import { Star, Users } from 'lucide-react';
import { PollResults as PollResultsType, getPollResults } from '../../services/polls';

interface PollResultsProps {
  pollId: string;
}

export default function PollResults({ pollId }: PollResultsProps) {
  const [results, setResults] = useState<PollResultsType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await getPollResults(pollId);
        setResults(r);
      } catch {
        // Error loading results
      } finally {
        setLoading(false);
      }
    })();
  }, [pollId]);

  if (loading) {
    return <div className="text-sm text-gray-500 py-4 text-center">Loading results...</div>;
  }

  if (!results || results.totalResponses === 0) {
    return <div className="text-sm text-gray-500 py-4 text-center">No responses yet</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Users className="w-4 h-4" />
        <span>{results.totalResponses} response{results.totalResponses !== 1 ? 's' : ''}</span>
      </div>

      {results.questionResults.map((qr) => (
        <div key={qr.questionId} className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{qr.questionText}</p>

          {/* Multiple Choice - Bar Chart */}
          {qr.questionType === 'multiple-choice' && qr.optionCounts && (
            <div className="space-y-1.5">
              {[...qr.optionCounts.entries()].map(([option, count]) => {
                const pct = results.totalResponses > 0 ? Math.round((count / results.totalResponses) * 100) : 0;
                return (
                  <div key={option} className="space-y-0.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-700 dark:text-gray-300">{option}</span>
                      <span className="text-gray-500">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Yes/No */}
          {qr.questionType === 'yes-no' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Yes: {qr.yesCount || 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">No: {qr.noCount || 0}</span>
              </div>
              {results.totalResponses > 0 && (
                <div className="flex-1">
                  <div className="w-full bg-red-200 dark:bg-red-900/30 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-green-500 h-3 rounded-l-full transition-all"
                      style={{ width: `${Math.round(((qr.yesCount || 0) / results.totalResponses) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rating */}
          {qr.questionType === 'rating' && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }, (_, i) => (
                  <Star key={i} className={`w-4 h-4 ${
                    (qr.ratingAverage || 0) >= i + 1
                      ? 'text-yellow-400 fill-yellow-400'
                      : (qr.ratingAverage || 0) >= i + 0.5
                      ? 'text-yellow-400 fill-yellow-400 opacity-50'
                      : 'text-gray-300 dark:text-gray-600'
                  }`} />
                ))}
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {(qr.ratingAverage || 0).toFixed(1)}
              </span>
              <span className="text-xs text-gray-500">avg</span>
            </div>
          )}

          {/* Free Text */}
          {qr.questionType === 'free-text' && qr.freeTextResponses && (
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {qr.freeTextResponses.map((text, i) => (
                <div key={i} className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded px-3 py-1.5">
                  "{text}"
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
